import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { defaultFilterValues } from "resources/constants";
import ColorPicker from "shared/components/ColorPicker";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import useTranslation from "shared/helpers/hooks/useTranslation";
import { requiredRules } from "shared/helpers/validators";
import ButtonLoader from "shared/ui/ButtonLoader";
import TextInput from "shared/ui/TextInput";
import { ERequestStatus } from "store/enums/index.enum";
import { ITemplate } from "store/interfaces/campaignDetails";
import {
  CreateSurveyTemplate,
  GetTemplates,
  UpdateSurveyTemplate,
  UpdateSurveyTemplateDefault,
  selectSurveyInfo,
} from "store/slicers/campaignDetail";
import {
  selectButtonLoadingState,
  setButtonLoading,
} from "store/slicers/common";
import UploadImage from "./UploadImage";

interface IFormData {
  name: string;
  buttonColor: string;
  buttonTextColor: string;
  questionColor: string;
  answerColor: string;
  imageBase64: string;
  logoImageBase64: string;
  answerTextColor: string;
}

const defaultValues: IFormData = {
  name: "",
  buttonColor: "#DDDCDC",
  buttonTextColor: "#F0F0F0",
  questionColor: "#4A4A4A",
  answerColor: "#DDDCDC",
  logoImageBase64: "",
  imageBase64: "",
  answerTextColor: "#DDDCDC",
};

const AddEditTemplate = ({
  onSuccess,
  editData,
}: {
  onSuccess;
  editData: ITemplate;
}) => {
  const surveyInfo = useSelector(selectSurveyInfo);
  const { id } = useParams();
  const dispatch = useAsyncDispatch();
  const t = useTranslation();
  const btnLoading = useSelector(selectButtonLoadingState);
  const methods = useForm({
    defaultValues,
  });

  const handleChange = useCallback(
    (name: keyof IFormData, { hex }) => {
      methods.setValue(name, hex);
    },
    [methods]
  );

  const init = useCallback(() => {
    if (editData) {
      methods.reset({
        name: editData.name,
        buttonColor: editData.buttonColor || defaultValues.buttonColor,
        buttonTextColor:
          editData.buttonTextColor || defaultValues.buttonTextColor,
        questionColor: editData.questionColor || defaultValues.questionColor,
        answerColor: editData.answerColor || defaultValues.answerColor,
        logoImageBase64: editData.logoImageBase64,
        imageBase64: editData.imageBase64,
        answerTextColor:
          editData?.answerTextColor || defaultValues.answerTextColor,
      });
    }
  }, [editData, methods]);

  const handleChangeImage = (val, name) => {
    methods.setValue(name, val);
  };

  useEffect(() => {
    init();
  }, [init]);

  const onSubmit = async (formData: IFormData) => {
    dispatch(setButtonLoading(true));

    if (editData) {
      let result;

      if (editData.isDefault) {
        result = await dispatch(
          UpdateSurveyTemplateDefault({
            data: {
              logoImage: {
                base64Image: formData?.logoImageBase64,
                extension: "",
                removeImage: false,
              },
              image: {
                base64Image: formData?.imageBase64,
                extension: "",
                removeImage: false,
              },
              name: formData.name,
              questionColor: formData?.questionColor,
              answerColor: formData?.answerColor,
              buttonColor: formData?.buttonColor,
              buttonTextColor: formData?.buttonTextColor,
              publicTemplateID: Number(surveyInfo.template?.publicTemplateID),
            },
            id: editData.id,
          })
        );
      } else {
        result = await dispatch(
          UpdateSurveyTemplate({
            data: formData,
            id: editData.id,
          })
        );
      }

      if (result.meta.requestStatus !== ERequestStatus.FULFILLED) {
        dispatch(setButtonLoading(false));
        return;
      }
    } else {
      const result = await dispatch(CreateSurveyTemplate(formData));

      if (result.meta.requestStatus !== ERequestStatus.FULFILLED) {
        dispatch(setButtonLoading(false));
        return;
      }
    }

    dispatch(GetTemplates({ formData: defaultFilterValues, id: Number(id) }));
    dispatch(setButtonLoading(false));
    onSuccess?.();
  };

  return (
    <Box>
      <FormProvider {...methods}>
        <Box mb={2}>
          <TextInput name="name" label={"Name"} rules={requiredRules} />
        </Box>
      </FormProvider>
      <Box
        display="flex"
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography fontSize={16}>{t("question_color")}</Typography>
        <ColorPicker
          name="questionColor"
          color={methods.watch("questionColor")}
          onChange={handleChange}
        />
      </Box>
      <Box
        display="flex"
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography fontSize={16}>{t("answer_color")}</Typography>
        <ColorPicker
          name="answerColor"
          color={methods.watch("answerColor")}
          onChange={handleChange}
        />
      </Box>
      {/* <Box
        display="flex"
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography fontSize={16}>{t("answer_text_color")}</Typography>
        <ColorPicker
          name="answerTextColor"
          color={methods.watch("answerTextColor")}
          onChange={handleChange}
        />
      </Box> */}

      <Box
        display="flex"
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography fontSize={16}>{t("button_color")}</Typography>
        <ColorPicker
          name="buttonColor"
          color={methods.watch("buttonColor")}
          onChange={handleChange}
        />
      </Box>
      <Box
        display="flex"
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography fontSize={16}>{t("button_text_color")}</Typography>
        <ColorPicker
          name="buttonTextColor"
          color={methods.watch("buttonTextColor")}
          onChange={handleChange}
        />
      </Box>
      <Box>
        <UploadImage
          name="logoImageBase64"
          title="logo_image"
          val={methods.watch("logoImageBase64")}
          onChange={handleChangeImage}
        />
      </Box>
      <Box>
        <UploadImage
          name="imageBase64"
          title="main_image"
          val={methods.watch("imageBase64")}
          onChange={handleChangeImage}
        />
      </Box>
      <Box display="flex" justifyContent={"flex-end"} mt={2}>
        <ButtonLoader
          variant="contained"
          onClick={methods.handleSubmit(onSubmit)}
          isLoading={btnLoading}
        >
          <Typography>{t("save")}</Typography>
        </ButtonLoader>
      </Box>
    </Box>
  );
};

export default AddEditTemplate;
